import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { handleAuthRedirect, AuthProvider } from '@micronet/auth';
import { ErrorBoundary, installReporter } from '@micronet/clientreports';
import { AuthBridge } from './AuthBridge';
import { AUTH, ENV } from './config/env';
import { LoginRoute, HomeRoute } from './routes';
import './index.css';

// At module scope, so a crash during module evaluation or inside
// handleAuthRedirect() is still reported; moving this into renderApp would miss
// exactly those failures.
//
// The endpoint is absolute because the SPA and the API are different origins
// (app.<dominio> vs api.<dominio>): a relative path posts at the CloudFront
// distribution, whose SPA fallback answers 200 index.html and swallows the report.
// No auth provider exists yet, so AuthBridge registers the token getter later.
//
// extraHeaders carries the gateway claims the rest of the SPA already sends
// (see api/client.ts): the lean local stack has no gateway, so without them the
// backend rejects every report and the feature cannot be exercised on a developer's
// machine. Gated on import.meta.env.DEV, which Vite replaces with a literal `false`
// in a production build — so this drops out of the shipped bundle entirely rather
// than riding along inert, which is what the gateway would overwrite anyway.
installReporter({
  endpoint: `${ENV.conciliatronApi}/api/v1/client-reports`,
  extraHeaders: import.meta.env.DEV
    ? {
        'X-Authz-User-Id': ENV.authzUserId,
        ...(ENV.workspaceId !== undefined
          ? { 'X-Authz-Workspace-Ids': String(ENV.workspaceId) }
          : {}),
      }
    : undefined,
});

function renderApp() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <AuthProvider config={AUTH}>
          <AuthBridge />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LoginRoute />} />
              <Route path="/home" element={<HomeRoute />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

// MSAL 5.x redirect bridge: if the URL carries an auth response (popup or
// top-level redirect fallback), let the bridge handle it before rendering.
// For popup: broadcasts result via BroadcastChannel, then window.close().
// For redirect: stores payload in sessionStorage and navigates to origin.
handleAuthRedirect()
  .then((handled) => {
    if (!handled) renderApp();
  })
  .catch(() => renderApp());
